// ============================================================================
// Hash router with auth guards.
//
// Routes are async: a view fetches its own data before it renders, so we never
// paint a dashboard full of zeroes and then snap it to real numbers.
//
// The guards here are UX, not security. A creator who edits the hash to reach
// #/admin gets a redirect; a creator who bypasses this file entirely still
// gets nothing, because RLS refuses the rows. Both layers exist on purpose.
// ============================================================================

import { getSession, getProfile, isAdmin, handleAuthCallback } from './lib/auth.js';
import { mountShellRegions, setActiveNav } from './views/shell.js';
import { initMotion } from './lib/motion.js';
import { recordVisit } from './api/creator.js';

const routes = [];

function route(path, loader, opts = {}) {
  routes.push({ path, loader, ...opts });
}

// ---- public ---------------------------------------------------------------
route('/',          () => import('./views/public/landing.js'));
route('/apply',     () => import('./views/public/apply.js'));
route('/login',     () => import('./views/public/login.js'));
// Public by necessity: it is reached from an email footer by somebody who
// is almost certainly not signed in. The token in the URL is the credential.
route('/unsubscribe', () => import('./views/public/unsubscribe.js'));
route('/auth/callback', () => import('./views/public/callback.js'));

// ---- creator (requires an approved profile) -------------------------------
route('/dashboard', () => import('./views/creator/dashboard.js'), { requires: 'creator' });
route('/missions',  () => import('./views/creator/missions.js'),  { requires: 'creator' });
route('/drops',     () => import('./views/creator/drops.js'),     { requires: 'creator' });
route('/rewards',   () => import('./views/creator/rewards.js'),   { requires: 'creator' });
route('/profile',   () => import('./views/creator/profile.js'),   { requires: 'creator' });

// ---- admin ----------------------------------------------------------------
route('/admin',             () => import('./views/admin/overview.js'),    { requires: 'admin' });
route('/admin/creators',    () => import('./views/admin/creators.js'),    { requires: 'admin' });
route('/admin/applications',() => import('./views/admin/applications.js'),{ requires: 'admin' });
route('/admin/submissions', () => import('./views/admin/submissions.js'), { requires: 'admin' });
route('/admin/missions',    () => import('./views/admin/missions.js'),    { requires: 'admin' });
route('/admin/drops',       () => import('./views/admin/drops.js'),       { requires: 'admin' });
route('/admin/shipments',   () => import('./views/admin/shipments.js'),   { requires: 'admin' });
route('/admin/reasons',     () => import('./views/admin/reasons.js'),     { requires: 'admin' });
route('/admin/team',        () => import('./views/admin/team.js'),        { requires: 'admin' });
route('/admin/banned',      () => import('./views/admin/banned.js'),      { requires: 'admin' });

let currentToken = 0;

export function navigate(path, { replace = false } = {}) {
  const target = `#${path}`;
  if (window.location.hash === target) return handleRoute();
  if (replace) window.location.replace(target);
  else window.location.hash = target;
}

function parseHash() {
  const raw = window.location.hash.replace(/^#/, '') || '/';
  const [path, query] = raw.split('?');
  return {
    path: path.replace(/\/+$/, '') || '/',
    params: new URLSearchParams(query || '')
  };
}

export async function handleRoute() {
  // Guard against a slow route resolving after a newer navigation started.
  const token = ++currentToken;

  const outlet = document.getElementById('app');
  if (!outlet) return;

  // In-page fragment anchors (#shop, #top, the landing chapter dots) are NOT
  // routes. Every app route starts with '#/', so a bare fragment is a scroll
  // target — once something is on screen, leave it to native browser scrolling
  // instead of parsing it as a route and bouncing to '/'.
  const raw = window.location.hash.replace(/^#/, '');

  // GoTrue's fallback redirect (rejected or missing redirect_to) lands here
  // with the outcome as a bare fragment, not a route: #error=access_denied&
  // error_code=otp_expired... on failure, #access_token=... on success. Read
  // it before the anchor guard below can swallow it, or the user gets bounced
  // to the landing page with no idea their sign-in link failed.
  if (raw && !raw.startsWith('/') && /(^|&)(error|error_code|access_token)=/.test(raw)) {
    const frag = new URLSearchParams(raw);
    if (frag.has('access_token')) {
      // detectSessionInUrl already consumed the tokens on client init; the
      // callback route just waits for the session and routes onward.
      return navigate('/auth/callback', { replace: true });
    }
    const message = frag.get('error_code') === 'otp_expired'
      ? 'That sign-in link has expired. Request a new one.'
      : (frag.get('error_description') || 'Sign-in failed. Request a new link.');
    return navigate(`/login?error=${encodeURIComponent(message)}`, { replace: true });
  }

  if (raw && !raw.startsWith('/') && outlet.innerHTML.trim()) return;

  const { path, params } = parseHash();
  const match = routes.find((r) => r.path === path);

  // The callback route must run before any guard: it is what creates the
  // session the guards are about to check for.
  if (path === '/auth/callback') {
    const result = await handleAuthCallback(params);
    if (token !== currentToken) return;
    if (result.ok) return afterLogin();
    return navigate(`/login?error=${encodeURIComponent(result.message)}`, { replace: true });
  }

  if (!match) return navigate('/', { replace: true });

  // ---- guards -------------------------------------------------------------
  if (match.requires) {
    const session = await getSession();
    if (token !== currentToken) return;

    if (!session) {
      return navigate(`/login?next=${encodeURIComponent(path)}`, { replace: true });
    }

    if (match.requires === 'admin') {
      const ok = await isAdmin();
      if (token !== currentToken) return;
      if (!ok) return navigate('/dashboard', { replace: true });
    }

    if (match.requires === 'creator') {
      const profile = await getProfile();
      if (token !== currentToken) return;
      // Signed in but no approved profile: pending, denied, or never applied.
      if (!profile) return navigate('/apply?status=noprofile', { replace: true });
      if (profile.status !== 'approved') {
        return navigate(`/login?status=${profile.status}`, { replace: true });
      }

      // Attendance. Here rather than in afterLogin() because a magic-link
      // session lasts a long time: most visits arrive on a session that is
      // already valid and never touch the callback route at all, and those
      // are exactly the visits an admin means by "are they still using it".
      // Deliberately not awaited — see recordVisit().
      recordVisit();
    }
  }

  // ---- render -------------------------------------------------------------
  // Scroll-snap is a landing-page behavior. Clear it on every navigation so
  // it can never follow someone into the dashboard, where snapping a data
  // table to the top of the viewport would be maddening. The landing view
  // re-adds it in its own mount().
  document.documentElement.classList.remove('dw-snap');

  outlet.setAttribute('aria-busy', 'true');
  try {
    const mod = await match.loader();
    if (token !== currentToken) return;

    const ctx = { params, path, navigate };
    const markup = await mod.render(ctx);
    if (token !== currentToken) return;

    outlet.innerHTML = String(markup);
    mod.mount?.(ctx);
    setActiveNav(path);
    window.scrollTo(0, 0);
    // Re-arm scroll reveals, counters and pointer effects for the new markup.
    initMotion(outlet);
  } catch (err) {
    console.error('[router]', path, err);
    if (token !== currentToken) return;
    outlet.innerHTML = errorView(err);
  } finally {
    if (token === currentToken) outlet.removeAttribute('aria-busy');
  }
}

/** Send a freshly signed-in user wherever they belong. */
async function afterLogin() {
  const next = sessionStorage.getItem('dw_next');
  sessionStorage.removeItem('dw_next');

  if (next) return navigate(next, { replace: true });
  if (await isAdmin()) return navigate('/admin', { replace: true });

  const profile = await getProfile({ refresh: true });
  if (profile?.status === 'approved') return navigate('/dashboard', { replace: true });
  return navigate('/login?status=' + (profile?.status ?? 'noprofile'), { replace: true });
}

function errorView(err) {
  return `
    <div class="dw-container dw-section">
      <div class="dw-panel dw-panel--pad">
        <h2 class="dw-h4">Something broke</h2>
        <p class="dw-muted">${String(err?.message ?? err).replace(/[<>&]/g, '')}</p>
        <a class="dw-btn dw-btn--secondary" href="#/">Back to safety</a>
      </div>
    </div>`;
}

export async function initRouter() {
  await mountShellRegions();
  window.addEventListener('hashchange', handleRoute);
  await handleRoute();
}
